import { useEffect } from 'react';
import useCustomerSubmission from './useCustomerSubmission.js';

// The customer's ORIGINAL trade-in submission for this quote, read-only. Replaces
// the manual `select items from public_api.customer_submission ...` lookup: it shows
// what the customer typed (make/model, declared condition, qty, and the free-text
// "anything else we should know?") before the drain reshaped it into the quote.
//
// Quote-level and as-submitted: the items are shown in their original order with no
// attempt to line them up against the current quote items, so nothing can drift onto
// the wrong row. `submission: null` is the empty state (no submission on file), not
// an error.
const CHANNEL_LABEL = { c2b: 'Website', bulk: 'Bulk upload' };

function submittedName(it) {
    const name = [it.make, it.model].filter(s => s && s.trim()).join(' ').trim();
    return name || 'Unnamed item';
}

// `submissionState` is the page's own read (useCustomerSubmission), passed in so
// opening the dialog does not fetch it again. Without it the dialog reads itself.
export default function CustomerSubmissionDialog({ dealId, quoteId, submissionState, onClose }) {
    const fetched = useCustomerSubmission(dealId, quoteId, { enabled: !submissionState });
    const state = submissionState || fetched;

    useEffect(() => {
        const onKey = e => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const sub = state.submission;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content qa-submission" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>What the customer submitted</h2>
                    <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
                </div>
                <div className="modal-body-scroll">
                    {state.loading && <div className="qa-muted">Loading…</div>}
                    {state.error && <div className="qa-error" role="alert">{state.error}</div>}

                    {!state.loading && !state.error && !sub && (
                        <div className="qa-muted">No original submission on file for this quote.</div>
                    )}

                    {!state.loading && !state.error && sub && (
                        <>
                            <div className="qa-sub-meta qa-muted">
                                Submitted via {CHANNEL_LABEL[sub.channel] || sub.channel || 'unknown'}
                                {sub.reference_code ? ` · ${sub.reference_code}` : ''}
                            </div>
                            {sub.notes && (
                                <div className="qa-sub-notes">
                                    <div className="qa-sub-notes-k">Customer note</div>
                                    <div className="qa-note-text">{sub.notes}</div>
                                </div>
                            )}
                            {sub.items.map((it, i) => (
                                <div key={i} className="qa-sub-row">
                                    <div className="qa-sub-name">{submittedName(it)}</div>
                                    <div className="qa-sub-facts qa-muted">
                                        {[it.category, it.condition, `qty ${it.quantity ?? 1}`]
                                            .filter(Boolean).join(' · ')}
                                    </div>
                                    {it.description && <div className="qa-sub-desc">{it.description}</div>}
                                </div>
                            ))}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
